import { SurveyDeadlineStats } from '@g17eco/types/initiativeStats';
import { DashboardSection } from '@g17eco/molecules/dashboard';
import { useMemo } from 'react';
import Chart from 'react-google-charts';
import variables from '../../../../css/variables.module.scss';
import { QUESTION } from '@constants/terminology';
import { AdminSectionTitle } from './AdminSectionTitle';

const DEADLINE_REMINDERS =
  'These charts display the progress of reports based on their deadlines. They reflect the first instance a report meets the criteria, retaining the original date even if a later metric is rejected.';

interface DeadlineRemindersProps {
  surveyStats: SurveyDeadlineStats[];
}

type DeadlineReminderCategory =
  | 'fourteenDaysBefore'
  | 'sevenDaysBefore'
  | 'threeDaysBefore'
  | 'exceededDeadline'
  | 'notMetCriteria'
  | 'noDeadline';

interface DeadlineReminderStats {
  answered: {
    [key in DeadlineReminderCategory]: number;
  };
  verified: {
    [key in DeadlineReminderCategory]: number;
  };
  completed: {
    [key in DeadlineReminderCategory]: number;
  };
}

const chartOptions = {
  pieHole: 0.75,
  pieSliceText: 'none',
  legend: { position: 'bottom', alignment: 'center', textStyle: { fontSize: 9 }, maxLines: 10 },
  chartArea: { width: '100%', height: '80%' },
  colors: [
    variables.ChartDark,
    variables.ChartMedium,
    variables.ChartLight,
    variables.ChartPale,
    variables.ChartRed,
    variables.ChartBaseline,
  ],
};

const categorizeByDeadline = ({
  daysToDeadline,
  deadlineDate,
}: {
  daysToDeadline: number | null;
  deadlineDate: string | undefined;
}): DeadlineReminderCategory => {
  switch (true) {
    case deadlineDate === null:
      return 'noDeadline';
    case Number(daysToDeadline) >= 14:
      return 'fourteenDaysBefore';
    case Number(daysToDeadline) >= 7:
      return 'sevenDaysBefore';
    case Number(daysToDeadline) > 0:
      return 'threeDaysBefore';
    case Number(daysToDeadline) < 0:
      return 'exceededDeadline';
    default:
      return 'notMetCriteria';
  }
};

const calculateDeadlineStats = (surveys: SurveyDeadlineStats[]): DeadlineReminderStats => {
  const stats: DeadlineReminderStats = {
    answered: {
      fourteenDaysBefore: 0,
      sevenDaysBefore: 0,
      threeDaysBefore: 0,
      notMetCriteria: 0,
      exceededDeadline: 0,
      noDeadline: 0,
    },
    verified: {
      fourteenDaysBefore: 0,
      sevenDaysBefore: 0,
      threeDaysBefore: 0,
      notMetCriteria: 0,
      exceededDeadline: 0,
      noDeadline: 0,
    },
    completed: {
      fourteenDaysBefore: 0,
      sevenDaysBefore: 0,
      threeDaysBefore: 0,
      notMetCriteria: 0,
      exceededDeadline: 0,
      noDeadline: 0,
    },
  };

  surveys.forEach(({ answeredDaysToDeadline, verifiedDaysToDeadline, completedDaysToDeadline, deadlineDate }) => {
    stats.answered[categorizeByDeadline({ daysToDeadline: answeredDaysToDeadline, deadlineDate })]++;
    stats.verified[categorizeByDeadline({ daysToDeadline: verifiedDaysToDeadline, deadlineDate })]++;
    stats.completed[categorizeByDeadline({ daysToDeadline: completedDaysToDeadline, deadlineDate })]++;
  });

  return stats;
};

export const DeadlineReminders = ({ surveyStats }: DeadlineRemindersProps) => {
  const stats = useMemo(() => calculateDeadlineStats(surveyStats), [surveyStats]);

  const answeredData = [
    ['Category', 'Count'],
    ['14 days before', stats.answered.fourteenDaysBefore],
    ['7 days before', stats.answered.sevenDaysBefore],
    ['3 days before', stats.answered.threeDaysBefore],
    ['Marked after deadline', stats.answered.exceededDeadline],
    ['Not fully answered', stats.answered.notMetCriteria],
    ['No deadline', stats.answered.noDeadline],
  ];

  const verifiedData = [
    ['Category', 'Count'],
    ['14 days before', stats.verified.fourteenDaysBefore],
    ['7 days before', stats.verified.sevenDaysBefore],
    ['3 days before', stats.verified.threeDaysBefore],
    ['Marked after deadline', stats.verified.exceededDeadline],
    ['Not fully verified', stats.verified.notMetCriteria],
    ['No deadline', stats.verified.noDeadline],
  ];

  const completedData = [
    ['Category', 'Count'],
    ['14 days before', stats.completed.fourteenDaysBefore],
    ['7 days before', stats.completed.sevenDaysBefore],
    ['3 days before', stats.completed.threeDaysBefore],
    ['Marked after deadline', stats.completed.exceededDeadline],
    ['Not complete', stats.completed.notMetCriteria],
    ['No deadline', stats.completed.noDeadline],
  ];

  return (
    <DashboardSection padding={0} title={<AdminSectionTitle title='Deadline reminders' tooltip={DEADLINE_REMINDERS} />}>
      <div className='row mb-4'>
        <div className='col-4'>
          <div className='d-flex flex-column align-items-center'>
            <div className='fw-bold'>
              All {QUESTION.PLURAL} <span className='text-ThemeIconPrimary'>answered</span> before deadline
            </div>
            <Chart chartType='PieChart' width='100%' height='auto' data={answeredData} options={chartOptions} data-testid='deadline-answered-chart' />
          </div>
        </div>
        <div className='col-4'>
          <div className='d-flex flex-column align-items-center'>
            <div className='fw-bold'>
              All {QUESTION.PLURAL} <span className='text-ThemeSuccessMedium'>verified</span> before deadline
            </div>
            <Chart chartType='PieChart' width='100%' height='auto' data={verifiedData} options={chartOptions} data-testid='deadline-verified-chart' />
          </div>
        </div>
        <div className='col-4'>
          <div className='d-flex flex-column align-items-center'>
            <div className='fw-bold'>
              Marked <span className='text-ThemeHeadingMedium'>completed</span> before deadline
            </div>
            <Chart chartType='PieChart' width='100%' height='auto' data={completedData} options={chartOptions} data-testid='deadline-completed-chart' />
          </div>
        </div>
      </div>
    </DashboardSection>
  );
};
